/**
 * Colors that are part of the main color palette should be defined in "base".
 * If we have variations of the base colors, ligther or darker versions, those should
 * go under "shades".
 *
 * Palette: Avida. Three brand anchors carry the theme —
 *   lime #CBF976 (primary action), mint #C7ECE4 (chrome/surface), ink #16201D (text/dark rail).
 * Neutrals are green-tinted rather than blue-tinted so greys sit under the brand
 * instead of fighting it. Token names are historical: read the comment, not the name
 * (e.g. "secondary" is no longer blue, "primary1" is no longer sand).
 *
 * Semantic state colors (danger / warning / success shades) are deliberately NOT
 * rotated into the green family — they carry meaning, including traffic-light
 * triads like ColorCode.GREEN/YELLOW/RED, and must stay distinguishable from a
 * brand accent that is now itself green.
 */
const base = {
  // main brand colors
  primary: '#CBF976', // lime
  secondary: '#2F6F5C', // deep green
  secondary2: '#16201D', // ink
  secondary3: '#C7ECE4', // mint
  alpha: '#FFFFFF', // white
  beta: '#16201D', // ink - near black
  gamma: '#F4F7F5', // grey
  success: '#A8E6B8', // green - kept lighter than lime so status reads as status
  support: '#F3F6F4', // grey
  danger: '#EA5B63', // red
  warning: '#BD795A', // orange
};

const shades = {
  primary1: '#96BCA1', // sage - outbound (coach) chat bubble, accent text on dark
  primary2: '#B4EC57', // lime deep
  primary3: '#E9FBCB', // lime tint
  primary4: '#C2F764', // lime
  primary5: '#A9CE84', // sage
  primary6: '#8DAE68', // sage dark
  primary7: '#F3FDE2', // lime wash
  primary8: '#E8F7D0', // lime wash
  secondary1: '#235C4B', // deep green dark 40%
  secondary2: '#2F6F5C', // deep green - positive values, links
  secondary3: '#0F1A16', // ink deep - text
  secondary4: '#C7ECE4', // mint
  secondary5: '#EEF8F3', // light mint
  secondary6: '#ADCEC7', // dark mint
  secondary7: '#D3E9E0', // borders
  secondary8: '#24352F', // border dark
  secondary9: '#FECCCC', // pinkish
  secondary10: '#F7FAF8', // even lighter mint
  secondary11: '#8FBE3F', // lime - white with opacity 0.1
  // The only blue left in the palette, and deliberate. Weight progression pairs gain
  // against loss, and both readings have to be told apart at a glance; with every other
  // token now in the green family there is no second hue to carry that. Deep enough to
  // clear AA on the stat block, which the light blue it replaces did not.
  info1: '#2F6BD3', // blue - gain/loss pairing on weight progression
  // Chat bubbles. The coach's own messages carry the brand sage; the client's carry blue, so
  // the two voices separate by hue and not only by which side of the column they sit on. Both are
  // tints of info1 rather than a second blue hue, keeping the palette at one blue.
  info2: '#C1D3F2', // blue light - inbound (client) chat bubble
  info3: '#8DAEE7', // blue mid - quoted message inside an inbound bubble
  alpha1: '#FAFEFC', // light gray
  alpha2: '#F7FAF8', // light gray
  gamma1: '#E0E3E1', // grey - ink with opacity 0.1
  gamma2: '#8B9E93', // grey - ink with opacity 0.4
  gamma3: '#C5C7C6', // grey border
  gamma4: '#EEF1F0', // grey box-shadow
  gamma5: '#EBEFED', // grey background in input
  gamma6: '#92A69A', // gray subscription price
  gamma7: '#6A7970', // gray sticky faded out section - meets 4.6:1 on white
  gamma8: '#CFD2D0', // gray separation line
  gamma9: '#BBBDBC', // gray MUI placeholder
  gamma10: '#65726A', // gray text
  gamma11: '#7B8C82', // gray shade
  gamma12: '#E7EAE8', // tag grey
  gamma13: '#DCDFDD', // tag border grey
  gamma14: '#74847B', // dark gray text
  danger1: '#EC6B74', // danger - white with opacity 0.1
  danger2: '#FDEFEF', // danger with opacity 0.1
  danger3: '#EB1C26', // danger with a more bright red
  danger4: '#FFD2D2', // danger with light red
  danger5: '#FF5A5F', // danger - a lighter red color
  beta1: '#3E4F4A', // ink variation
  beta2: '#1E2A26', // ink - white with opacity 0.1
  success1: '#86CC47', // ligth green
  success2: '#4DB1C1', // green - for success message
  success3: '#00AE07',
  success4: '#4fd44c', // success tag border
  success5: '#5de15c', // success tag bg
  success6: '#10A643', // ok tag bg
  success7: '#017E2B', // ok tag border
  success8: '#48D767', // progression accent — matches completed-workout checkmark stroke
  warning1: '#FFF8DE', // orange - very light
  warning2: '#FFDD64', // orange - light
  warning3: '#FFA763', // orange - dark
  dark1: '#121A17', // dark - ink with opacity 0.1
  dark2: '#3E4F4A', // dark - ink with opacity 0.4
  dark3: '#0F1614', // dark - ink with opacity 0.65
  dark4: '#B6B8B7',
  dark5: '#1B241F', // dark - ink with opacity 0.85
};

/**
 * This function adds an alpha value (transparency) to a given hexadecimal color
 * @param {string} hexColor hexadecimal 6 digit color (#FFFFFF)
 * @param {number} alpha alpha value, between 0 and 1
 * @returns rgba color
 */
const addAlpha = (hexColor, alpha) => {
  const radix = 16;
  const [, red, green, blue] = hexColor.match(/#([\da-f]{2})([\da-f]{2})([\da-f]{2})/i);
  return `rgba(${parseInt(red, radix)},${parseInt(green, radix)},${parseInt(blue, radix)},${alpha})`;
};

const chartColors = [
  '#8884d8',
  '#82ca9d',
  '#ffc658',
  '#4176DF',
  '#93BD31',
  '#7E20AD',
  '#66D1D4',
  '#FD765D',
  '#A071FF',
  '#F7A0EF',
  '#12659B',
  '#CF6C17',
  '#259982',
  '#FAC400',
  '#D6064B',
  '#2A75DF',
];

// Theme primary color mapping using theme names as keys
const themeColorMapping = {
  'Tangy Tangerine': '#FFB947',
  'Bubblegum Flamingo': '#FFACC0',
  'Ultra Violet': '#ECACE1',
  'Electric Blue': '#8EBFEC',
  'Neon Mint': '#BAF59E',
  'Platinum Slate': '#C5C9DA',
  'Predator Green': '#839F76',
  'Blueberry Milkshake': '#7792BD',
  'Tuscany Tide': '#E38A8A',
  'Varsity Blue': '#4782BD',
};

const colors = {
  base,
  shades,
  addAlpha,
  chartColors,
};

/**
 * Convert hex color to RGB values
 * @param {string} hex - Hex color code
 * @returns {Object} RGB values
 */
const hexToRgb = (hex) => {
  const cleanHex = hex.replace('#', '');
  // Parse hex values to RGB
  return {
    r: parseInt(cleanHex.substring(0, 2), 16),
    g: parseInt(cleanHex.substring(2, 4), 16),
    b: parseInt(cleanHex.substring(4, 6), 16),
  };
};

const getLuminance = (hexColor) => {
  const rgb = hexToRgb(hexColor);

  const [r, g, b] = [rgb.r, rgb.g, rgb.b].map((channel) => {
    const c = channel / 255;
    return c <= 0.03928
      ? c / 12.92
      : ((c + 0.055) / 1.055) ** 2.4;
  });

  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/**
 * Returns a contrast-safe color for text based on a background.
 *
 * @param {string} backgroundHex - Background color in hex
 * @param {string} colorToUse - Preferred color
 * @param {string} fallbackColor - Fallback color if contrast is insufficient
 * @returns {string} Contrast-safe  color
 */
const getContrastSafeColor = (backgroundHex, colorToUse, fallbackColor) => {
  const bgLuminance = getLuminance(backgroundHex);
  const textLuminance = getLuminance(colorToUse);

  const contrastRatio = (Math.max(bgLuminance, textLuminance) + 0.05) / (Math.min(bgLuminance, textLuminance) + 0.05);

  return contrastRatio >= 4.5 ? colorToUse : fallbackColor;
};

export default colors;
export { themeColorMapping, getContrastSafeColor };
